/**
 * One row of a store file's rules in Address Aliases: a location text that
 * file said on the latest run, the lot it became, and which rule did it.
 * "Edit" opens an inline editor where the operator picks the lot and whether
 * the rule covers only this store's file or every store's file (Ian,
 * 2026-09-23) — the "main lot" case, where one word means a different place
 * in each store's export. Changes reload the page data, because a rule edit
 * can move texts between rules in several files at once.
 */
import React, { useState } from "react"

import {
  PhotoListLot,
  PhotoListRuleScope,
  PhotoListStargardAlias,
  PhotoListStargardSeenText,
  createStargardAlias,
  deleteStargardAlias,
  updateStargardAlias,
} from "../../api/photoList"
import { THEME } from "../../config/theme"
import {
  addButtonStyle,
  cellTextStyle,
  countStyle,
  deleteLinkStyle,
  editorStyle,
  hintTextStyle,
  inputStyle,
  radioLabelStyle,
  rawTextStyle,
  ruleGridStyle,
  smallButtonStyle,
} from "./stargardAliasStyles"

/** The every-store scope value — matches the backend's any-file "". */
export const EVERY_STORE_FILE = ""

/** What a text with no rule prints — the generator's raw[:20] fallback. */
export const NO_RULE_RESULT = "the text itself (first 20 characters) instead of a lot name"

/** Which store files' texts a rule covered on the latest run. */
export type RuleUse = { file: string; cars: number }

interface Props {
  row: PhotoListStargardSeenText
  /** The store file this row sits under ("Toyota", "Dodge", ...). */
  file: string
  /** False for a source the server couldn't match to one of the six store
   * files: a rule can't be scoped to it, so only "every store" is offered. */
  fileScopeAvailable: boolean
  lots: Array<PhotoListLot & { id: number }>
  /** Every alias row — to find the every-store rule for the same text. */
  aliases: PhotoListStargardAlias[]
  /** Store files each rule covered on the latest run, by alias id — so the
   * editor can say who else a shared rule reaches before it changes. */
  usesByAliasId: Map<number, RuleUse[]>
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  onStructureChanged: () => void | Promise<void>
  onError: (message: string | null) => void
}

export function scopeLabel(scope: PhotoListRuleScope, file: string): string {
  return scope === "this_file" ? `${file} only` : "Every store"
}

function describeFiles(uses: RuleUse[]): string {
  return uses.map((use) => `${use.file}'s file (${use.cars} car${use.cars === 1 ? "" : "s"})`).join(", ")
}

export default function StargardSeenRuleRow({
  row,
  file,
  fileScopeAvailable,
  lots,
  aliases,
  usesByAliasId,
  editable,
  isMobile,
  dealerGroupId,
  onStructureChanged,
  onError,
}: Props) {
  const [editing, setEditing] = useState(false)
  const [draftLotId, setDraftLotId] = useState(String(row.lot_id ?? ""))
  const [draftScope, setDraftScope] = useState<PhotoListRuleScope>(row.rule_scope ?? "every_store")
  const [saving, setSaving] = useState(false)

  const canEdit = editable && row.alias_id !== null && row.rule_scope !== null
  const canonicalText = row.raw.trim().toLowerCase()
  const everyStoreRule = aliases.find(
    (alias) => alias.stargard_rooftop === EVERY_STORE_FILE && alias.raw_text === canonicalText,
  )
  const everyStoreUses =
    everyStoreRule?.id != null ? usesByAliasId.get(everyStoreRule.id) ?? [] : []

  // Switching a store's own rule to "every store" starts from the SHARED
  // rule's lot: saving as-is then just drops this store's rule, and other
  // stores' cars only move if the operator deliberately picks a new lot.
  const chooseScope = (scope: PhotoListRuleScope) => {
    setDraftScope(scope)
    if (row.rule_scope === "this_file" && scope === "every_store" && everyStoreRule?.lot_id != null) {
      setDraftLotId(String(everyStoreRule.lot_id))
    } else if (scope === row.rule_scope) {
      setDraftLotId(String(row.lot_id ?? ""))
    }
  }

  const openEditor = () => {
    setDraftLotId(String(row.lot_id ?? ""))
    setDraftScope(row.rule_scope ?? "every_store")
    setEditing(true)
  }

  const run = async (change: () => Promise<unknown>, failure: string) => {
    setSaving(true)
    onError(null)
    try {
      await change()
      setEditing(false)
      await onStructureChanged()
    } catch (err) {
      // A two-step change can fail halfway (the create landed, the delete
      // didn't): reload so the page and a retry see what actually happened —
      // BEFORE showing the error, since a successful reload clears errors.
      await onStructureChanged()
      onError(err instanceof Error ? err.message : failure)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => {
    const lotId = Number.parseInt(draftLotId, 10)
    if (row.alias_id === null || Number.isNaN(lotId)) return
    const aliasId = row.alias_id
    const lotChanged = lotId !== row.lot_id
    run(async () => {
      if (row.rule_scope === draftScope) {
        // Same scope: just re-point the rule (an every-store rule changes for
        // every file it covers — the editor says which, before saving).
        if (lotChanged) await updateStargardAlias(aliasId, { lot_id: lotId }, dealerGroupId)
      } else if (draftScope === "this_file") {
        // Every-store -> this file only: add a rule for this file; the other
        // files keep the every-store rule untouched.
        await createStargardAlias(row.raw, lotId, file, dealerGroupId)
      } else {
        // This file only -> every store: one rule for all files. Re-point (or
        // create) the every-store rule, then drop this file's own rule.
        if (everyStoreRule?.id != null) {
          if (lotId !== everyStoreRule.lot_id) {
            await updateStargardAlias(everyStoreRule.id, { lot_id: lotId }, dealerGroupId)
          }
        } else {
          await createStargardAlias(row.raw, lotId, EVERY_STORE_FILE, dealerGroupId)
        }
        await deleteStargardAlias(aliasId, dealerGroupId)
      }
    }, "Failed to save the rule")
  }

  const handleDelete = () => {
    if (row.alias_id === null) return
    const aliasId = row.alias_id
    const fallback =
      row.rule_scope === "this_file" && everyStoreRule
        ? `${file}'s cars will use the every-store rule ("${everyStoreRule.lot_name}").`
        : `Cars with this text will show ${NO_RULE_RESULT}.`
    const others =
      row.rule_scope === "every_store" && row.also_used_by.length > 0
        ? ` It also covers ${describeFiles(row.also_used_by)}.`
        : ""
    // The confirm says which rule — "main lot" can have one per store.
    const which = row.rule_scope === "this_file" ? `${file}-only` : "every-store"
    if (!window.confirm(`Delete the ${which} rule for "${row.raw}"?${others} ${fallback}`)) return
    run(() => deleteStargardAlias(aliasId, dealerGroupId), "Failed to delete the rule")
  }

  const scopeText = row.rule_scope ? scopeLabel(row.rule_scope, file) : ""
  const editButton = canEdit ? (
    <button type="button" onClick={openEditor} disabled={editing} style={smallButtonStyle(editing)}>
      Edit
    </button>
  ) : (
    <span />
  )

  return (
    <>
      {isMobile ? (
        <div style={ruleGridStyle(true)}>
          <div>
            <div style={rawTextStyle}>{row.raw}</div>
            <div style={{ ...cellTextStyle, marginTop: THEME.spacing.xs }}>
              → {row.lot_name} <span style={countStyle}>· {scopeText}</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: THEME.spacing.xs }}>
            <span style={countStyle}>{row.cars} cars</span>
            {editButton}
          </div>
        </div>
      ) : (
        <div style={ruleGridStyle(false)}>
          <span style={rawTextStyle}>{row.raw}</span>
          <span style={cellTextStyle}>{row.lot_name}</span>
          <span style={countStyle}>{row.cars}</span>
          <span style={cellTextStyle}>{scopeText}</span>
          {editButton}
        </div>
      )}

      {editing && (
        <div style={editorStyle}>
          <label style={{ ...cellTextStyle, display: "flex", alignItems: "center", gap: THEME.spacing.sm, flexWrap: "wrap" }}>
            <span style={rawTextStyle}>{row.raw}</span> →
            <select
              value={draftLotId}
              onChange={(e) => setDraftLotId(e.target.value)}
              aria-label={`Lot for ${row.raw}`}
              style={inputStyle}
            >
              {lots.map((lot) => (
                <option key={lot.id} value={lot.id}>
                  {lot.name}
                </option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", flexWrap: "wrap", gap: THEME.spacing.md }} role="radiogroup" aria-label="Applies to">
            <span style={cellTextStyle}>Applies to:</span>
            {fileScopeAvailable && (
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  name={`scope-${file}-${row.raw}`}
                  checked={draftScope === "this_file"}
                  onChange={() => chooseScope("this_file")}
                />
                Only {file}'s file
              </label>
            )}
            <label style={radioLabelStyle}>
              <input
                type="radio"
                name={`scope-${file}-${row.raw}`}
                checked={draftScope === "every_store"}
                onChange={() => chooseScope("every_store")}
              />
              Every store's file
            </label>
          </div>

          <EditorConsequence
            row={row}
            file={file}
            draftScope={draftScope}
            draftLotName={lots.find((lot) => String(lot.id) === draftLotId)?.name ?? row.lot_name ?? ""}
            everyStoreRule={everyStoreRule}
            everyStoreUses={everyStoreUses}
          />

          <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm, flexWrap: "wrap" }}>
            <button type="button" onClick={handleSave} disabled={saving} style={addButtonStyle(saving)}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving} style={smallButtonStyle(saving)}>
              Cancel
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={handleDelete} disabled={saving} style={deleteLinkStyle}>
              Delete rule
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/** One plain sentence on what saving will do — the part that used to be
 * invisible (an every-store rule quietly covering other stores' files). */
function EditorConsequence({
  row,
  file,
  draftScope,
  draftLotName,
  everyStoreRule,
  everyStoreUses,
}: {
  row: PhotoListStargardSeenText
  file: string
  draftScope: PhotoListRuleScope
  /** The lot picked in the editor right now. */
  draftLotName: string
  everyStoreRule: PhotoListStargardAlias | undefined
  everyStoreUses: RuleUse[]
}) {
  let text: string
  if (row.rule_scope === "every_store" && draftScope === "every_store") {
    text =
      row.also_used_by.length > 0
        ? `This rule also covers ${describeFiles(row.also_used_by)} — a new lot applies there too.`
        : "No other store's file used this rule on the last run."
  } else if (draftScope === "this_file" && row.rule_scope === "every_store") {
    text = `Adds a rule for ${file}'s file only ("${draftLotName}"); other stores keep "${row.lot_name}".`
  } else if (draftScope === "every_store" && everyStoreRule) {
    // This store's own rule -> the shared rule that already exists.
    const reach =
      everyStoreUses.length > 0
        ? ` It also covers ${describeFiles(everyStoreUses)}.`
        : " No other store's file used it on the last run."
    text =
      draftLotName === everyStoreRule.lot_name
        ? `Drops ${file}'s own rule; ${file}'s cars use the every-store rule ("${everyStoreRule.lot_name}").${reach}`
        : `Drops ${file}'s own rule and points the every-store rule at "${draftLotName}" ` +
          `(it was "${everyStoreRule.lot_name}"): every store's file that uses it moves there.${reach}`
  } else if (draftScope === "every_store") {
    text =
      `Makes this one rule for every store's file, so every store whose file says ` +
      `"${row.raw}" and has no rule of its own will use it too.`
  } else {
    text = `Only ${file}'s file uses this rule.`
  }
  return <p style={hintTextStyle}>{text}</p>
}
